import type {
	ChartDataInterface as DsChartDataInterface,
	ChartStatisticRangePeriod,
} from '@Shelly/shelly-design-system';
import type { ChartDataInterface as AppChartDataInterface } from 'src/types/Chart';

const RANGE_PERIOD_ALIASES: Record<string, ChartStatisticRangePeriod> = {
	'24_hours': '24_hours',
	day: 'day',
	hour: 'hour',
	'7_days': '7_days',
	week: 'week',
	'30_days': '30_days',
	month: 'month',
	year: 'year',
	'6_months': '6_months',
	'12_months': '12_months',
	billing_date: 'billing_date',
	custom: 'custom',
	day_of_week: 'week',
};

const SERIES_LABELS: Record<string, string> = {
	consumption: 'Consumption',
	reversed: 'Returned to grid',
	total_house_energy: 'Consumption',
	generated_energy: 'Generated',
	cost: 'Cost',
};

const seriesHasValues = (series: { data?: { value?: number }[] } | undefined) =>
	(series?.data ?? []).some((point) => Math.abs(Number(point?.value ?? 0)) > 0);

const normalizeSeriesPoint = (
	seriesName: string,
	point: AppChartDataInterface['series'][0]['data'][0],
) => {
	const rawValue = Number(point.value ?? 0);
	const isReversed = /(reversed|returned)/.test(seriesName);
	return {
		dt: point.dt,
		value: isReversed ? Math.abs(rawValue) : rawValue,
		cost: point.cost,
		reversed: point.reversed,
		convertCost: point.convertCost,
		metaData: point.metaData,
		missing: point.missing,
	};
};

const resolveRangePeriod = (chartData: AppChartDataInterface): ChartStatisticRangePeriod => {
	const raw = chartData.rangePeriod ?? 'custom';
	return RANGE_PERIOD_ALIASES[raw] ?? (raw as ChartStatisticRangePeriod);
};

const resolveRangeInterval = (
	chartData: AppChartDataInterface,
): DsChartDataInterface['rangeInterval'] => {
	const interval = chartData.rangeInterval;
	if (interval === 'minute' || interval === 'hour' || interval === 'day' || interval === 'month') {
		return interval;
	}
	return undefined;
};

/** Convert legacy Chart.js statistics output into DS LayerChart contract. */
export const toDsChartData = (
	chartData: AppChartDataInterface | null | undefined,
	options: { compact?: boolean } = {},
): DsChartDataInterface | null => {
	if (!chartData?.series?.length) return null;

	const visibleSeries = chartData.series.filter((series) => {
		if (series.visible === false || series.disabled || !series.data?.length) return false;
		if (/(reversed|returned)/.test(series.name) && !seriesHasValues(series)) return false;
		return true;
	});
	if (!visibleSeries.length) return null;

	const seriesLabels = Object.fromEntries(
		visibleSeries
			.map((series) => [series.name, SERIES_LABELS[series.name] ?? series.name])
			.filter(([name]) => Boolean(name)),
	);

	return {
		id: chartData.id ?? chartData.name,
		name: chartData.name,
		type: chartData.type === 'pie' ? 'pie' : chartData.type,
		units: chartData.units,
		height: chartData.height || 240,
		labels: chartData.labels,
		keys: (chartData as AppChartDataInterface & { keys?: string[] }).keys,
		timezone: chartData.timezone,
		rangePeriod: resolveRangePeriod(chartData),
		rangeInterval: resolveRangeInterval(chartData),
		thumbMode: options.compact || chartData.thumbMode,
		seriesLabels: Object.keys(seriesLabels).length ? seriesLabels : undefined,
		series: visibleSeries.map((series) => ({
			name: series.name,
			style: series.style,
			visible: series.visible !== false,
			prevPeriod: series.prevPeriod,
			colors: series.colors,
			data: (series.data ?? []).map((point) => normalizeSeriesPoint(series.name, point)),
		})),
	} as DsChartDataInterface;
};

export const toPieChartData = (params: {
	labels: string[];
	keys: string[];
	values: number[];
	colors: string[];
	units?: string;
	name?: string;
}): DsChartDataInterface =>
	({
		name: params.name ?? 'consumerMix',
		type: 'pie',
		units: params.units ?? 'W',
		labels: params.labels,
		keys: params.keys,
		loaded: params.labels.length > 0,
		height: 240,
		series: [
			{
				name: params.name ?? 'consumerMix',
				visible: true,
				data: params.values.map((value, index) => ({
					dt: index,
					value,
				})),
				colors: params.colors,
				style: 'none',
			},
		],
	}) as DsChartDataInterface;

export type ChartLegendItem = {
	id: string;
	label: string;
	color: string;
};

const GENERATION_SERIES_META: Record<string, { labelKey: string; defaultLabel: string; color: string }> =
	{
		consumption: {
			labelKey: 'chart_legend_consumption',
			defaultLabel: 'Consumption',
			color: 'var(--color-chart-consumed-energy)',
		},
		reversed: {
			labelKey: 'chart_legend_reversed',
			defaultLabel: 'Returned to grid',
			color: 'var(--color-chart-generated-energy)',
		},
		total_house_energy: {
			labelKey: 'chart_legend_consumption',
			defaultLabel: 'Consumption',
			color: 'var(--color-chart-consumed-energy)',
		},
		generated_energy: {
			labelKey: 'chart_legend_generated_energy',
			defaultLabel: 'Generated',
			color: 'var(--color-chart-generated-energy)',
		},
	};

export const buildGenerationLegend = (
	chartData: DsChartDataInterface | null,
	formatLabel: (key: string, fallback: string) => string,
): ChartLegendItem[] =>
	(chartData?.series ?? [])
		.filter((series) => series.visible !== false && seriesHasValues(series))
		.map((series) => {
			const meta = GENERATION_SERIES_META[series.name];
			return {
				id: series.name,
				label: formatLabel(meta?.labelKey ?? series.name, meta?.defaultLabel ?? series.name),
				color: meta?.color ?? 'var(--color-primary)',
			};
		});
